import styled from "styled-components/macro";
import { theme } from "ui/theme";
import PolicyLayout, {
  $PageTitle,
  $TextContainer,
  $OverviewText,
  $SectionTitle,
  $SubsectionTitle,
  $Paragraph,
  $UnorderedList,
  $ListItem,
  $SummaryBox,
  $SummaryBoxTitle,
  $UnderlinedText,
  $UserAgreementLink,
  $CodeOfConductLink,
  $ExternalLink,
  $SecurityWhitepaperLink,
  $CookiePolicyLink,
  $ThirdPartyServiceProvidersLink,
  $PrivacyAtAlongEmailLink,
} from "templates/PolicyLayout";

import HeartLock from "images/heart-lock.png";
import HeartLock2x from "images/heart-lock@2x.png";
import SingleUnderlineSun from "images/single-underline-sun.png";

const $PageTitleTextDecorator = styled.span`
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0.34rem;
    left: 0.6rem;
    right: 1.5rem;
    height: 1.3rem;
    background-image: url(${SingleUnderlineSun});
    background-repeat: no-repeat;
    background-size: contain;
    z-index: ${theme.zIndexBelow};

    @media screen and (${theme.BreakpointTabletDown}) {
      display: none;
    }
  }
`;

const PageTitle = () => {
  return (
    <$PageTitle>
      <$PageTitleTextDecorator>Privacy</$PageTitleTextDecorator> Policy
    </$PageTitle>
  );
};

const AlongMarketingSiteLink = () => {
  return (
    <$ExternalLink href="https://www.along.org" linkText="www.along.org" />
  );
};

const AlongMarketingWebsitePrivacyPolicyLink = () => {
  return (
    <$ExternalLink
      href="https://www.along.org/website-privacy-policy/"
      linkText="Website Privacy Notice"
    />
  );
};

export default function PrivacyPolicy() {
  return (
    <PolicyLayout
      pageTitle={<PageTitle />}
      lastUpdatedDate="December 19, 2023"
      src={HeartLock}
      srcSet={HeartLock2x}
      alt="a graphic illustration of a lock with a heart shaped key hole"
    >
      <$TextContainer>
        <$OverviewText size="h3">
          <$Paragraph>
            We have done our best to write this Privacy Policy in simple and
            clear terms. We have also added summaries below each section that
            provide short explanations of the legal language in plain English.
          </$Paragraph>
        </$OverviewText>

        <$SectionTitle size="h1">Introduction</$SectionTitle>

        <$Paragraph>
          Along is a free, web-based, digital teacher-student connection builder
          - helping teachers to better understand their students and increase
          student engagement in the classroom. The Services (defined below)
          enabled by Along have been developed to facilitate meaningful
          communication (in the form of multiple choice questions, asynchronous
          text, video and/or other supported methods of communication) between
          teachers and students through research-informed reflection questions
          and resources.
        </$Paragraph>
        <$Paragraph>
          Gradient Learning (“<strong>Gradient Learning</strong>”, “
          <strong>we</strong>”, “<strong>us</strong>” and “<strong>our</strong>
          ”), a California nonprofit public benefit organization and 501(c)(3)
          nonprofit organization, is dedicated to bringing communities, schools,
          and families together in pursuit of meeting the needs of every
          student. With support from the Chan Zuckerberg Initiative (“
          <strong>CZI</strong>”), Gradient Learning offers Along as a free
          service to teachers and students. For clarity, this Privacy Policy
          does not apply to any other product or service offered by Gradient
          Learning, such as Summit Learning, which is offered under its
          own&nbsp;
          <$ExternalLink
            href="https://www.summitlearning.org/privacy-center/privacy-policy"
            linkText="Privacy Policy"
          />
          . Further, this notice does not apply to personal information relating
          to our employees, contractors, or other personnel.
        </$Paragraph>
        <$Paragraph>
          Gradient Learning and CZI are signatories to the Future of Privacy
          Forum’s and SIAA’s Student&nbsp;
          <$ExternalLink
            href="https://studentprivacypledge.org/privacy-pledge-2-0/"
            linkText="Privacy Pledge"
          />
          . Along also participates in, and meets the standards of, the&nbsp;
          <$ExternalLink
            href="https://ikeepsafe.org/certification/coppa/"
            linkText="iKeepSafe Safe Harbor program"
          />
          .
        </$Paragraph>
        <$Paragraph>
          <strong>
            Gradient Learning does not place ads in Along nor use personal
            information of users we have identified as Student Users for ad
            purposes, including for behaviorally-targeted advertising
            purposes.&nbsp;
            <$UnderlinedText>
              We do not, and will not, make or seek to make money from students
              or their schools, teachers, or parents. We do not, have not, and
              will not sell, rent, or lease the personal information of Student
              Users.
            </$UnderlinedText>
          </strong>
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Along is a free teacher-student connection builder, provided by
            Gradient Learning with support from the Chan Zuckerberg Initiative.
            Along is designed to help build trust between teachers and their
            students, and to help increase student engagement in the classroom.
          </$Paragraph>
          <$Paragraph>
            Your privacy on Along is our priority. We don’t make money from
            students and teachers using Along, nor do we allow ads to be placed
            on Along. We do not and will never sell or rent personal information
            of users identified as Student Users.
          </$Paragraph>
        </$SummaryBox>
        <$Paragraph>
          As with any technology used in the classroom, protections must be in
          place to safeguard student information. We have established this
          Privacy Policy (the “<strong>Privacy Policy</strong>”) to explain the
          personal information that we collect and how we protect, use, and
          share it.
        </$Paragraph>
        <$Paragraph>
          This Privacy Policy applies to Along’s features and related services
          (collectively, the “<strong>Services</strong>”). It also applies
          anywhere we gather personal information and refer to this Privacy
          Policy. This Privacy Policy applies to teachers who register for the
          Services (“<strong>Registered Teacher</strong>”) and students invited
          by a Registered Teacher who register for the services (“
          <strong>Student User</strong>”) (together, “<strong>Users</strong>” or
          “<strong>You</strong>”), administrators and school district officials
          from Schools (defined below) (“
          <strong>School Administrators</strong>”) whose teachers and students
          are Users, and parents and legal guardians of a Student User who is
          under the age of 18 (“<strong>Parents</strong>”).
        </$Paragraph>
        <$Paragraph>
          For clarity, Along’s website [
          <AlongMarketingSiteLink />
          ] is subject to its own&nbsp;
          <AlongMarketingWebsitePrivacyPolicyLink />.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We take your privacy very seriously. This Privacy Policy covers the
            protection of personal information for students and teachers who use
            Along, school administrators of schools where those teachers work,
            and parents/legal guardians of students.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">1. Contract</$SectionTitle>

        <$Paragraph>
          When you use our Services, you agree to all the terms in this Privacy
          Policy and our&nbsp;
          <$UserAgreementLink />, including the Data Privacy Addendum (“
          <strong>User Agreement</strong>”). You agree that by registering for,
          accessing or using our Services, you are entering into a legally
          binding contract with Gradient Learning, as further described in the
          User Agreement.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            By using Along, you are entering into a contract with Gradient
            Learning and agreeing to the terms in this Privacy Policy and
            our&nbsp;
            <$UserAgreementLink />.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">2. Website Visitors</$SectionTitle>

        <$Paragraph>
          If you are a visitor to the Along website [
          <AlongMarketingSiteLink />
          ], we will collect certain information from you in order to provide
          our services. This includes information you give us directly, such as
          your first and last name, and information provided from your browser
          or device, like log data and cookies. Please see the Along&nbsp;
          <AlongMarketingWebsitePrivacyPolicyLink />
          &nbsp; for more information.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We collect and use information from Along website visitors so that
            we can send requested information. This collection and use is
            subject to its own&nbsp;
            <AlongMarketingWebsitePrivacyPolicyLink />.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">3. Users of the Services</$SectionTitle>

        <$Paragraph>
          We provide the Services directly to Registered Teachers or School
          Administrators who agree to the&nbsp;
          <$UserAgreementLink />
          &nbsp;on behalf of, and as an authorized agent of, the school through
          which they conduct learning activities (“<strong>School</strong>”). In
          order to operate the Services, we collect certain information and only
          use and share the information to provide the Services in accordance
          with the User Agreement (or other terms agreed upon with Registered
          Teachers or School Administrators), this Privacy Policy, and
          applicable laws. See Section 3.1 below for details.
        </$Paragraph>
        <$Paragraph>
          The User Agreement includes a Data Privacy Addendum that includes
          protections for Student User information and limits our use and
          sharing of Student User personal information to the Services.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We provide Along to teachers and/or school admin users who agree to
            our&nbsp;
            <$UserAgreementLink />. To operate the Services, we need to collect
            and use certain personal information from users (in accordance with
            our User Agreement, this Privacy Policy, and applicable laws). Any
            personal information we collect comes with specific protections and
            sharing limitations.
          </$Paragraph>
        </$SummaryBox>

        <$SubsectionTitle size="h3">
          3.1 Information We Collect
        </$SubsectionTitle>

        <$Paragraph>
          We collect the following types of information, including personal
          information, from students and teachers when they sign up for and use
          the Services.
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            <strong>Account Sign-Up and Profile Information:</strong>
            <$UnorderedList>
              <$ListItem>
                Teacher and student contact information such as full name,
                preferred name/nickname, email address and school name
              </$ListItem>
              <$ListItem>
                Teacher and student profile information, such as a profile
                picture and descriptions of hobbies and favorite activities
              </$ListItem>
              <$ListItem>
                Any log-in details a teacher or student chooses to provide
                (e.g., email address)
              </$ListItem>
            </$UnorderedList>
          </$ListItem>
          <$ListItem>
            <strong>Use of the Services:</strong>
            <$UnorderedList>
              <$ListItem>
                <$UnderlinedText>
                  Content and online communications
                </$UnderlinedText>
                : Content (including teacher prompts, student responses, and
                teacher feedback to students), online communications (such as
                written or audio messages, photos, or videos that are exchanged
                between students and teachers within the Services), and type of
                communication
              </$ListItem>
              <$ListItem>
                <$UnderlinedText>Communications metadata</$UnderlinedText>:
                Information such as file size, name of a file (e.g., photo,
                audio or video), or date or time the audio or video recording
                was taken
              </$ListItem>
              <$ListItem>
                <$UnderlinedText>Usage</$UnderlinedText>: Information about how
                students and teachers use and interact with the Services, such
                as the prompts they search for and choose, number of photos,
                written messages, or audio/video recordings exchanged, whether
                they open emails or click the links contained in emails, and
                what types of features they engage with in the Services
              </$ListItem>
            </$UnorderedList>
          </$ListItem>
          <$ListItem>
            <strong>
              Class and Student User Information. Teachers may choose to import
              the below information from outside third party services into the
              Services:
            </strong>
            <$UnorderedList>
              <$ListItem>Class rosters, homeroom, and grade level</$ListItem>
              <$ListItem>Student course schedules, teacher names</$ListItem>
              <$ListItem>
                Student identification numbers such as school identification
                number or school information system identification number
              </$ListItem>
              <$ListItem>
                Academic or extracurricular activities a student may belong to
                or participate in
              </$ListItem>
            </$UnorderedList>
          </$ListItem>
          <$ListItem>
            <strong>User-submitted Reports, Surveys, and Feedback:</strong>
            <$UnorderedList>
              <$ListItem>
                Information related to bug and content reports, such as
                descriptions of the issue and communications between users and
                product support staff
              </$ListItem>
              <$ListItem>
                Survey answers and other product feedback you choose to provide
                us
              </$ListItem>
            </$UnorderedList>
          </$ListItem>
          <$ListItem>
            <strong>Parent and School Administrator Information:</strong>
            <$UnorderedList>
              <$ListItem>
                Names, phone numbers, and email addresses of parents and school
                administrators (as provided by teachers and students)
              </$ListItem>
            </$UnorderedList>
          </$ListItem>
          <$ListItem>
            <strong>Device and Network Information:</strong>
            <$UnorderedList>
              <$ListItem>
                We may collect information about devices (such as operating
                system, and browser type) and networks (such as language, IP
                address, internet service provider, and connection speed) to
                better distinguish users of the Services to deliver a
                customized, secure, and reliable experience for users
              </$ListItem>
            </$UnorderedList>
          </$ListItem>
          <$ListItem>
            <strong>Cookies, Pixel Tags, and Similar Technologies:</strong>
            <$UnorderedList>
              <$ListItem>
                Cookies. Cookies are small text files placed on your device
                consisting of a string of numbers and letters that uniquely
                identifies your device. This helps us do things such as save
                your preferences and identify Student Users in order to limit
                our collection and use of personal information accordingly.
              </$ListItem>
              <$ListItem>
                Pixels. Pixels are a type of technology placed on a website or
                within the body of an email for the purpose of tracking certain
                activity, such as views of a website or when an email is opened.
                Pixels are often used in combination with cookies. For
                additional information, see our Cookie Policy.
              </$ListItem>
              <$ListItem>
                Click-through URLs. If you “opt in” to receive newsletters,
                updates, or other information from us, our emails may use a
                “click-through URL” linked to content on our sites. When you
                click one of these URLs, they pass through a separate web server
                before arriving at the destination page on our sites. We use
                this click-through data to help us understand how recipients
                respond to, or interact with, our emails. If you prefer not to
                be tracked in this way, please do not click text or graphic
                links in emails you receive from us.
              </$ListItem>
            </$UnorderedList>
          </$ListItem>
        </$UnorderedList>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            When teachers and students sign up for Along, we collect certain
            types of information — including:
          </$Paragraph>
          <$UnorderedList>
            <$ListItem>
              Account sign-up and profile information (such as your name, email
              address, and profile picture)
            </$ListItem>
            <$ListItem>
              Your usage of Along (such as content you upload, like teacher
              prompts, student responses, and teacher feedback; written
              messages, photos or videos, and other communications; metadata;
              and usage data)
            </$ListItem>
            <$ListItem>
              Class and student user information (such as student names,
              classes, grade levels, teacher names, school ID numbers, and
              academic or extracurricular activities)
            </$ListItem>
            <$ListItem>
              User-submitted reports, surveys, and feedback (such as technical
              support data and communications, survey answers, and other product
              feedback)
            </$ListItem>
            <$ListItem>
              Parent and School Administrator information (such as contact
              information for parents and school administrators and parent ID
              numbers)
            </$ListItem>
            <$ListItem>
              Device and network information (such as operating systems, browser
              types, networks, and devices being used)
            </$ListItem>
            <$ListItem>
              Cookies, pixel tags, and similar technologies (such as cookies
              that help us save your preferences and identify users)
            </$ListItem>
          </$UnorderedList>
        </$SummaryBox>

        <$SubsectionTitle size="h3">
          3.2 Information We Do Not Seek to Collect or Store
        </$SubsectionTitle>

        <$Paragraph>
          We do not seek to collect sensitive information about Student Users or
          Registered Teachers, such as precise location or biometric data. If we
          become aware that such information has been provided, we will delete
          it within thirty (30) days of such awareness or get the appropriate
          School, Parent, or User consent to keep it if it is reasonably
          necessary for the Services.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We don’t intentionally collect sensitive information through Along,
            such as the exact location of students. If we become aware that
            sensitive information is on the Services, we will delete it or
            notify the appropriate party to get consent to keep it if necessary.
          </$Paragraph>
        </$SummaryBox>

        <$SubsectionTitle size="h3">
          3.3 We Limit the Use of the Information We Collect
        </$SubsectionTitle>

        <$Paragraph>
          We use the personal information we collect from the Services for
          educational purposes as directed by Registered Teachers or Schools,
          including to:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            Allow students and teachers to communicate with each other in the
            Services;
          </$ListItem>
          <$ListItem>
            Drive learning and mentoring engagement between students and
            teachers;
          </$ListItem>
          <$ListItem>
            Customize the experience for students and teachers, such as by
            suggesting prompts for teachers to use with students;
          </$ListItem>
          <$ListItem>
            Operate, develop, analyze, evaluate, and improve Gradient Learning’s
            educational sites, services, or applications;
          </$ListItem>
          <$ListItem>
            Communicate with Users, Parents, and School Administrators in
            connection with providing the Services;
          </$ListItem>
          <$ListItem>
            Communicate with teachers and school administrators for marketing
            purposes;
          </$ListItem>
          <$ListItem>
            Manage the oversight and use of the Services, pursuant to School
            policies;
          </$ListItem>
          <$ListItem>
            Maintain the security and reliability of the Services, troubleshoot,
            and fix bugs;
          </$ListItem>
          <$ListItem>
            Perform other activities requested by Registered Teachers or Schools
            for educational purposes or with the consent of a Parent or Student
            User of the age of majority;
          </$ListItem>
          <$ListItem>
            Protect or defend the rights, safety, or property of Gradient
            Learning, Schools or Users, or to comply with any law enforcement,
            legal, or reg$UnorderedListatory process;
          </$ListItem>
          <$ListItem>
            De-identify the information for other Gradient Learning purposes; or
          </$ListItem>
          <$ListItem>Comply with applicable laws.</$ListItem>
        </$UnorderedList>
        <$Paragraph>
          <$UnderlinedText>Safety</$UnderlinedText>. We are committed to making
          Along a safe space for students and teachers. Your well-being is very
          important to us. We also use the personal information we collect to
          improve safety for our Users on and off the Services. This includes
          helping Registered Teachers and School Administrators investigate
          suspicious activity, reported content, violations of School’s policies
          or our&nbsp;
          <$CodeOfConductLink linkText="policies" />; preventing spam and other
          bad experiences; combating harmful conduct; verifying accounts and
          activities; and maintaining the integrity of the Services. We may
          accomplish this through human review and automated systems.
        </$Paragraph>
        <$Paragraph>
          <$UnderlinedText>Advertising</$UnderlinedText>. Along does not include
          advertisements for third-party goods and services. However, we may use
          advertising to promote Along to teachers and school administrators,
          such as by using their email addresses or cookies to direct ads for
          Along to them on third-party sites, including third-party online
          services like LinkedIn.&nbsp;
          <strong>
            We do not use personal information of users identified as Student
            Users for ad purposes.
          </strong>
        </$Paragraph>
        <$Paragraph>
          As described in Along’s Website Privacy Notice,&nbsp;
          <strong>
            under California law, this practice is considered to be “sharing” of
            personal information and may also be deemed a “sale” of such
            information. You can opt-out of this practice by clicking the Do Not
            Sell or Share My Personal Information link or the link provided in
            the footer of the website in the Along&nbsp;
            <AlongMarketingWebsitePrivacyPolicyLink />
            &nbsp;and turn off the toggle switch for Marketing and Social Media
            Cookies. You may also be able to opt-out by broadcasting the Global
            Privacy Control (“GPC”) signal from a supported browser (see Section
            10.5 for more information).
          </strong>
        </$Paragraph>
        <$Paragraph>
          <$UnderlinedText>Teacher Library</$UnderlinedText>. The Along Teacher
          Library contains content provided by third party experts—specifically
          nonprofit organizations focused on education. Such content may include
          references and links to services provided by the nonprofit
          organizations whose privacy practices may differ from ours. Further,
          some of the content is provided or developed in collaboration with
          third-party experts and may be hosted on, or contain links to,
          third-party websites. If educators are interested, they may sign up
          for these additional third-party services or products, but this is not
          mandatory. If you choose to visit these sites or provide any
          additional personal information to these third parties, the
          information collected will be subject to their terms of use, data
          collection practices, and privacy policies.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We use the personal information collected from Along for educational
            purposes only, as directed by teachers and schools that use the
            Services. This personal information also helps us to improve safety
            for our users. We may use the personal information of teachers and
            school administrators to direct ads for Along on websites outside of
            Along. You may be able to have your browser block or delete cookies,
            but some parts of the services may not work properly as a result.
            Along also contains resources from nonprofit organizations; those
            resources may be hosted or contain links to third-party websites
            (and will be subject to their own terms of use, data collection
            practices, and privacy policies).
          </$Paragraph>
        </$SummaryBox>
        <$Paragraph>
          To be clear, we will <strong>NOT</strong>:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            Seek to make money from students or their schools, teachers, or
            parents/legal guardians through the Services;
          </$ListItem>
          <$ListItem>
            Include advertising in the Services, including behaviorally-targeted
            advertising to Users;
          </$ListItem>
          <$ListItem>
            Use communications content (e.g. audio or video recordings) for
            anything other than offering, improving, and securing the Services,
            which includes responding to and/or investigating user reports or
            other flags regarding such content;
          </$ListItem>
          <$ListItem>
            Sell, rent or lease (or authorize Service Providers to sell, rent or
            lease) any personal information we collect from Student Users for
            any purpose – including for advertising and marketing purposes;
          </$ListItem>
          <$ListItem>
            Disclose personal information to third parties except in a few
            limited cases such as to service providers who help us provide the
            Service, as required by law, or with your consent;
          </$ListItem>
          <$ListItem>
            Use (or authorize Service Providers to use) the personal information
            we collect from Users for the creation of commercial products or
            services; or
          </$ListItem>
          <$ListItem>
            Use information collected from Student Users for any purposes other
            than for educational purposes unless we have de-identified the
            information such that it cannot reasonably be linked to an
            identifiable Student User.
          </$ListItem>
        </$UnorderedList>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We don’t sell or use personal information to make money from
            students and teachers using Along, and we require our own service
            providers to make the same commitment. We also don’t use personal
            information of users we have identified as Student Users for
            anything other than educational purposes. In certain cases, we may
            use de-identified information, (i.e., information that cannot be
            used to identify or contact an individual) to improve your
            experience on Along.
          </$Paragraph>
        </$SummaryBox>
        <$Paragraph>
          <$UnderlinedText>De-identified information</$UnderlinedText>. We
          de-identify personal information and use de-identified information for
          other purposes, including research and product improvement of Gradient
          Learning’s educational sites, services, or applications. We consider “
          <strong>de-identified</strong>” information to be information that has
          direct and indirect personal identifiers removed such that the data
          cannot reasonably be used to identify or contact an individual. Such
          identifiers include persistent unique identifiers, name, ID numbers,
          and date of birth.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          3.4 We Limit How Personal Information Is Shared
        </$SubsectionTitle>

        <$Paragraph>
          In order to provide the Services, we share your personal information
          in the limited ways outlined below:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            <$UnderlinedText>Between students and teachers</$UnderlinedText>.
            Student Users and Registered Teachers will share content and
            messages with each other in the Services. Further, Registered
            Teachers’ personal information (e.g., names and profile pictures)
            will be shared with other Registered Teachers who are working with
            the same student, to support teacher coordination about overall
            student usage. The Services do not include features that make
            Student or Registered Teacher personal information publicly
            available.
          </$ListItem>
          <$ListItem>
            <$UnderlinedText>School Administrators</$UnderlinedText>. Because
            Along is an educational tool, authorized School Administrators from
            Schools may have legal rights to view, export, and request deletion
            of information in the Services. School Administrators may have
            access to information for a limited time period, even after a
            Student User or Registered Teacher has requested deletion of an
            account.
          </$ListItem>
          <$ListItem>
            <$UnderlinedText>Parents</$UnderlinedText>. We may share information
            with a Student User’s Parent as authorized by the student’s
            Registered Teacher or School Administrator or as required by
            applicable law.
          </$ListItem>
          <$ListItem>
            <$UnderlinedText>Service Providers</$UnderlinedText>. We use Service
            Providers to support the operation of the Services, such as through
            document management, data hosting, and provisioning customer service
            tools related to the Services. CZI is our long-term technology
            partner and works as a Service Provider to help us develop and
            support the Services. We do not and will not grant Service Providers
            the right to sell your personal information or to use it for any
            purpose other than what’s necessary to support the Services. We also
            require Service Providers to implement reasonable security
            practices. We maintain a list of our&nbsp;
            <$ThirdPartyServiceProvidersLink linkText="Service Providers" />
            &nbsp;(“<strong>Service Providers</strong>”), which may be updated
            from time to time.
          </$ListItem>
          <$ListItem>
            <$UnderlinedText>New owners</$UnderlinedText>. If the ownership or
            control of all or part of the Services changes, we may transfer
            information to that new owner. We, along with other signatories to
            the Student Privacy Pledge, have committed to protecting personal
            information of users identified as Student Users in the event of
            such a change in ownership so that we honor commitments made prior
            to the change. In the event of such a change and if the new owner
            does not agree to comply with this Privacy Policy, we will provide
            you notice and a chance to opt out before any information is
            transferred.
          </$ListItem>
          <$ListItem>
            <$UnderlinedText>Law enforcement requests</$UnderlinedText>. We may
            access, preserve, and share User information in response to a legal
            request (e.g., warrants, subpoenas, court orders), if we have a
            good-faith belief that the law requires us to do so. In such cases,
            to the extent permitted by law, we will attempt to provide the
            Student, School, Registered Teacher, School Administrator, or
            Parents (as applicable depending on circumstances) with notice of
            such legal request prior to complying with requests for User
            information.
          </$ListItem>
          <$ListItem>
            <$UnderlinedText>
              To coordinate with law enforcement to advance safety on and off
              the Services
            </$UnderlinedText>
            . We may also share User information when we have a good-faith
            belief it is necessary to: detect, prevent and address fraud,
            unauthorized use of Along, violations of School’s or our policies,
            to protect ourselves (including our rights, property or products),
            you or others; or to prevent death or imminent bodily harm.
          </$ListItem>
          <$ListItem>
            <$UnderlinedText>With other third parties</$UnderlinedText>. We may
            share User information with other third parties, if directed or
            authorized by the Registered Teacher or School, or with parental
            consent, to the extent permitted by law and subject to our&nbsp;
            <$UserAgreementLink />, including the Data Privacy Addendum.
          </$ListItem>
        </$UnorderedList>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We may share your information in limited ways with the following
            parties:
          </$Paragraph>
          <$UnorderedList>
            <$ListItem>
              Those who need it to use Along (for example, sharing information
              between teachers and students communicating with one another
              through Along).
            </$ListItem>
            <$ListItem>School administrators or parents.</$ListItem>
            <$ListItem>
              Third-party service providers that help us operate Along (Note: We
              never sell personal information of users identified as Student
              Users and require our third-party service providers to make the
              same commitment).
            </$ListItem>
            <$ListItem>
              Law enforcement, in response to a legal request or if we believe
              it’s necessary to ensure the safety of teachers and students on
              Along.
            </$ListItem>
            <$ListItem>
              Other third parties if directed or authorized by a teacher or
              school (with parental consent, and in accordance with the law and
              our <$UserAgreementLink />
              ).
            </$ListItem>
            <$ListItem>
              Any new owners of Along’s services (Note: if this happens, you’ll
              be able to opt out if the new owner does not follow this Privacy
              Policy).
            </$ListItem>
          </$UnorderedList>
        </$SummaryBox>
        <$Paragraph>
          <strong>De-identified information</strong>. We will only share
          de-identified information should we wish to demonstrate how the
          Services are used, its efficacy, or otherwise provide information or
          marketing materials related to the Services. We may share aggregated
          and/or non-personally identifiable information publicly (such as
          statistics about visitors and website traffic patterns).
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            To demonstrate the efficacy of Along or how it’s being used in
            classrooms and schools, we may publicly share information that has
            been de-identified (i.e., information that cannot be reasonably used
            to identify or contact an individual).
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">
          4. We Support Parent/Legal Guardian Engagement
        </$SectionTitle>

        <$Paragraph>
          We support and encourage the involvement of Parents in their Student
          Users’ education. Parents and Student Users may, at any time, make a
          request to access, review, correct, or delete personal information in
          the Services by contacting the Student User’s Registered Teacher or
          School. If the Registered Teacher or School determines that the
          request should be implemented, the Registered Teacher or School may
          direct us to make such a change. We will process such School requests
          within thirty (30) days of receiving a written request in a manner
          consistent with applicable law and the terms of the&nbsp;
          <$UserAgreementLink />
          &nbsp;(including the Data Privacy Addendum).
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We support and encourage parent/legal guardian involvement with
            Along. Students and parents/legal guardians may request to access,
            review, correct or delete personal information. Such requests must
            be made through the student’s teacher or school.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">
          5. Student User’s Personal Information
        </$SectionTitle>

        <$Paragraph>
          All students who use the Services will be invited by a Registered
          Teacher who has agreed to the&nbsp;
          <$UserAgreementLink />. Pursuant to the&nbsp;
          <$UserAgreementLink /> (including the Data Privacy Addendum), each
          Registered Teacher, on behalf of their School, has consented to our
          practices regarding the collection, use, and disclosure of personal
          information from Student Users as permitted by law.
        </$Paragraph>
        <$Paragraph>
          When the Registered Teacher invites their students to Along, we rely
          on consent obtained from the Registered Teacher or the School acting
          as an agent of the Student User’s parent or legal guardian in order to
          process the Student User’s personal information.
        </$Paragraph>
        <$Paragraph>
          Registered Teachers are also required to provide notice to School
          Administrators and are encouraged to notify Parents that they are
          using the Services to increase student engagement.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            To provide students with educational services, teachers and schools
            using Along have the authority to provide us access to their
            students’ personal information. Schools act as an agent of a
            student’s parent/legal guardian when they consent to our use of
            student personal information. When signing up for Along, teachers
            act on behalf of schools to review and agree to our&nbsp;
            <$UserAgreementLink />
            &nbsp;and this Privacy Policy, which details our practices of
            collecting, using, and disclosing students’ personal information.
            Teachers must also notify school administrators and parents/legal
            guardians that they are using Along with students.
          </$Paragraph>
        </$SummaryBox>
        <$Paragraph>
          COPPA governs the collection of certain information from children
          under the age of 13 (“child” or “children”). For more information
          about COPPA and generally protecting children’s online privacy, please
          visit the Federal Trade Commission&nbsp;
          <$ExternalLink
            href="https://www.ftc.gov/business-guidance/resources/complying-coppa-frequently-asked-questions"
            linkText="COPPA FAQs"
          />
          &nbsp;and&nbsp;
          <$ExternalLink
            href="https://consumer.ftc.gov/identity-theft-and-online-security/online-privacy-and-security"
            linkText="OnGuard Online"
          />
          . While COPPA does not directly apply to non-profit organizations such
          as Gradient Learning, we voluntarily comply with COPPA’s guidelines
          regarding students under the age of 13 as part of our commitment to
          transparency with parents regarding the collection and use of their
          child’s data.
        </$Paragraph>
        <$Paragraph>
          <strong>
            The commitments we make regarding information collected from or
            about students apply equally to all students regardless of their
            age. Accordingly, where this Privacy Policy references students or
            any information collected from or about students, our Privacy Policy
            applies to students under 13 years old as well as students 13 years
            old and above.
          </strong>
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We are committed to trying to protect the personal information for
            students of all ages on Along. As part of this commitment, we
            voluntarily comply with the core principles of COPPA.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">
          6. Security and Accuracy of Personal Information
        </$SectionTitle>

        <$Paragraph>
          The security of personal information from our Users is important to
          us, and we work hard to protect it from unauthorized access and use.
          In an effort to prevent unauthorized access, disclosure, or improper
          use of User information, and to maintain data accuracy, we have
          established physical, technical, and administrative safeguards
          designed to protect the personal information we collect. For
          additional technical details regarding Gradient Learning’s security
          programs and measures, please see our&nbsp;
          <$SecurityWhitepaperLink />.
        </$Paragraph>
        <$Paragraph>
          We’re constantly evolving our security procedures as technology
          changes, but our security procedures at a minimum include the
          following:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            We restrict access to personal information to authorized Gradient
            Learning employees, agents, service providers, or independent
            contractors who reasonably need to know that information in order to
            process it for us, and who are subject to confidentiality
            obligations. Employees and contractors (“<strong>Staff</strong>”)
            are subject to discipline if they fail to meet these obligations.
          </$ListItem>
          <$ListItem>
            We require our Service Providers with which we share Student User
            personal information to employ industry standard data protection and
            security protocols.
          </$ListItem>
          <$ListItem>
            We use strong authentication methods including multi-factor
            authentication for all Staff.
          </$ListItem>
          <$ListItem>
            We employ administrative, physical, and technical safeguards
            designed to protect personal information from unauthorized access,
            disclosure, and use or acquisition by an unauthorized person,
            including when transmitting and storing such information.
          </$ListItem>
          <$ListItem>
            We employ encryption technologies to securely transmit personal
            information, including data-in-transit encryption, and we encrypt
            personal information that is stored.
          </$ListItem>
          <$ListItem>
            We strive to maintain a data backup and recovery capability designed
            to help ensure a timely and accurate restoration of personal
            information.
          </$ListItem>
          <$ListItem>
            We work hard to maintain industry standard software development
            lifecycle with industry standard security practices designed to
            establish secure application, infrastructure, and network
            architectures.
          </$ListItem>
          <$ListItem>
            We endeavor to maintain event monitoring and response procedures for
            events which could impact functionality, security and/or
            availability of the Services.
          </$ListItem>
          <$ListItem>
            We provide Staff training for security incidents and maintain
            incident response policies, plans and procedures focused on timely
            and effective incident response.
          </$ListItem>
          <$ListItem>
            We employ trained security professionals with experience in security
            incident response and event monitoring.
          </$ListItem>
          <$ListItem>
            We perform application security testing (including penetration
            testing) and conduct security risk assessments focused on the
            identification and remediation of risks, and work hard to timely
            remediate identified security vulnerabilities.
          </$ListItem>
          <$ListItem>
            We implement oversight and governance procedures for security risks,
            including a vulnerability disclosure program and reviews of
            incidents affecting the Services.
          </$ListItem>
        </$UnorderedList>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We are constantly working on ways to prevent unauthorized access and
            misuse of personal information through administrative, physical, and
            technical safeguards. These include:
          </$Paragraph>
          <$UnorderedList>
            <$ListItem>using strong authentication methods,</$ListItem>
            <$ListItem>limiting who has access to student data,</$ListItem>
            <$ListItem>
              destroying or deleting personal information as needed,
            </$ListItem>
            <$ListItem>using strong encryption technology,</$ListItem>
            <$ListItem>
              using secured data backup and recovery capability,
            </$ListItem>
            <$ListItem>
              maintaining industry standard software development lifecycle,
            </$ListItem>
            <$ListItem>
              providing security training to employees and staff, and
            </$ListItem>
            <$ListItem>
              requiring Service Providers to follow similar terms.
            </$ListItem>
          </$UnorderedList>
          <$Paragraph>
            If there is a data breach, we will provide notice to the school as
            required by law.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">7. Data Retention</$SectionTitle>

        <$Paragraph>
          We will only retain personal information, including personal
          information from Users, for the time period required to support the
          authorized educational purposes. If a Student, Registered Teacher or
          School requests removal of any personal information or closure of
          their account, we will promptly direct our Staff to delete, dispose
          of, or de-identify the personal information.
        </$Paragraph>
        <$Paragraph>
          Following a Student User’s account deletion, we will notify their
          Registered Teacher and School Administrator, and the Student User
          account will be removed from Along after verifying the Student User’s
          request. Following a Registered Teacher’s account deletion request, we
          will notify their School that the Registered Teacher’s account, along
          with associated Student User accounts, will be removed from Along
          after verifying the Registered Teacher’s request.
        </$Paragraph>
        <$Paragraph>
          We will retain a Student User’s and Registered Teacher’s information
          for a 30-day record retention period in order to support the
          management by a School or Registered Teacher of information considered
          part of an education record. At the end of that record retention
          period, we will delete, dispose of, or de-identify the personal
          information within 30 days unless, consistent with applicable law,
          there is a legitimate reason to retain such personal information for a
          longer period.
        </$Paragraph>
        <$Paragraph>
          We may retain personal information for a longer time period if we have
          consent to retain such information or if we are required to retain
          such information to comply with our legal obligations or law
          enforcement requests, resolve disputes, protect the safety and
          security of our Users or our Services, or enforce the User Agreement
          (including the Data Privacy Addendum), other agreements, or any posted
          guidelines, policies or rules applicable to specific features of the
          Services. Such information will be deleted when it is no longer needed
          for the purpose for which it was retained.
        </$Paragraph>
        <$Paragraph>
          We will also delete Student User or Registered Teacher accounts after
          a period of inactivity.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We only keep personal information for as long as it’s necessary to
            provide Along. In certain cases, we may need to keep personal
            information if the law requires a different duration, or as directed
            by the teacher or school.
          </$Paragraph>
        </$SummaryBox>
        <$Paragraph>
          If you are a Parent and wish to have your student’s personal
          information removed from the Services, please contact your student’s
          Registered Teacher or School and review the information in Section 8
          (“Schools Can Delete Information”).
        </$Paragraph>

        <$SectionTitle size="h1">
          8. Schools Can Delete Information
        </$SectionTitle>

        <$Paragraph>
          Registered Teachers or School Administrators may request the review or
          deletion of their Student User information, or the closure of accounts
          associated with their School in the Services. At the end of the 30-day
          record retention period, we will delete or de-identify such
          information within 30 days, unless we otherwise have consent to retain
          such information or are required to retain such information to comply
          with a valid access or transfer request for education records, our
          legal obligations or law enforcement requests, resolve disputes, or
          enforce the&nbsp;
          <$UserAgreementLink />
          &nbsp;(including the Data Privacy Addendum),&nbsp;
          <$CodeOfConductLink />, or other policies or rules applicable to
          specific features of the Services. Such information will be deleted
          when it is no longer needed for the purpose for which it was retained.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Teachers and schools can request that we delete and update student
            personal information.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">
          9. Your Choices Regarding Your Personal Information
        </$SectionTitle>

        <$Paragraph>
          We provide a variety of ways for you to control your personal
          information, including choices about how we use that data. In some
          jurisdictions, these controls and choices may be enforceable as rights
          under applicable law.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          9.1 Access, portability, correction, and deletion
        </$SubsectionTitle>

        <$UnorderedList>
          <$ListItem>
            <$UnderlinedText>Access personal information</$UnderlinedText>. You
            can ask us for a copy of your personal information, including in a
            machine-readable form (data portability).
          </$ListItem>
          <$ListItem>
            <$UnderlinedText>
              Change or correct personal information
            </$UnderlinedText>
            . You can also ask us to change, update, or fix inaccurate personal
            information in certain cases, subject to our legal obligations and
            lawful exceptions.
          </$ListItem>
          <$ListItem>
            <$UnderlinedText>Delete personal information</$UnderlinedText>. You
            can ask us to erase or delete all or some of your personal
            information subject to our legal obligations and lawful exceptions.
          </$ListItem>
        </$UnorderedList>
        <$Paragraph>
          To make such requests, contact <$PrivacyAtAlongEmailLink />. In the
          email, please provide us with your name, state in which you live,
          which of the above rights you would like to exercise, and sufficient
          information that allows us to reasonably verify that you are the
          person about whom we collected personal information. Please also let
          us know if you have questions or concerns related to exercising any
          rights you have under applicable law.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          9.2 Object to, limit, or restrict use of personal information
        </$SubsectionTitle>

        <$UnorderedList>
          <$ListItem>
            <$UnderlinedText>Communications preferences</$UnderlinedText>. You
            can opt out from communications with Gradient Learning at any time
            via the unsubscribe link in emails from us.
          </$ListItem>
          <$ListItem>
            <$UnderlinedText>
              Targeted advertising and data sale / sharing
            </$UnderlinedText>
            . As described in Section 3.3 of this Privacy Policy, for visitors
            other than those identified as Student Users, we may use third-party
            advertising cookies on our Website in order to help us deliver
            advertising about Along on third-party sites. Some privacy laws
            define data “sales” broadly to include such practices (and under
            California law, this practice is considered to be “sharing” of
            personal information). You may opt out by using the controls
            described in Section 3.3 above or by using a browser Global Privacy
            Control as described below.
          </$ListItem>
          <$ListItem>
            <$UnderlinedText>
              Profiling and automated decisionmaking
            </$UnderlinedText>
            . Some privacy laws provide a right to opt-out of profiling or
            automated processing in furtherance of decisions that produce legal
            or similarly significant effects concerning the consumer. Gradient
            Learning does not engage in such profiling or automated processing.
          </$ListItem>
        </$UnorderedList>

        <$SubsectionTitle size="h3">
          9.3 Browser or platform controls
        </$SubsectionTitle>

        <$UnorderedList>
          <$ListItem>
            <$UnderlinedText>Cookie controls</$UnderlinedText>. Most web
            browsers are set to accept cookies by default. If you prefer, you
            can go to your browser settings to learn how to delete or reject
            cookies. If you choose to delete or reject cookies, this could
            affect certain settings or features of our website. Please see
            our&nbsp;
            <$CookiePolicyLink /> for more information.
          </$ListItem>
          <$ListItem>
            <$UnderlinedText>Global Privacy Control</$UnderlinedText>. Some
            browsers and browser extensions support the “Global Privacy Control”
            or similar controls that can send a signal to the websites you visit
            indicating your choice to opt out from certain types of data
            processing, including data sales and/or targeted advertising, as
            specified by applicable law. When we detect such a signal, we will
            make reasonable efforts to respect your choices indicated by a GPC
            setting or similar control that is recognized by regulation or
            otherwise widely acknowledged as a valid opt-out preference signal.
          </$ListItem>
          <$ListItem>
            <$UnderlinedText>Do Not Track</$UnderlinedText>. Some browsers
            include a “Do Not Track” (DNT) setting that can send a signal to the
            websites you visit indicating you do not wish to be tracked. Unlike
            the GPC described above, there is not a common understanding of how
            to interpret the DNT signal; therefore, our websites do not respond
            to browser DNT signals. Instead, you can use the range of other
            tools to control data collection and use, including the GPC, cookie
            controls, and advertising controls described above.
          </$ListItem>
        </$UnorderedList>
        <$Paragraph>
          If you would like to appeal a Gradient Learning decision with respect
          to a request to exercise any of your rights, please email us at&nbsp;
          <$PrivacyAtAlongEmailLink />
          &nbsp;and explain the basis for your appeal.
        </$Paragraph>
        <$Paragraph>
          Gradient Learning will not discriminate against you in any manner for
          exercising any of the above rights with respect to your personal
          information.
        </$Paragraph>

        <$SectionTitle size="h1">
          10. Additional Information for California Residents
        </$SectionTitle>

        <$Paragraph>
          While the CCPA does not apply to nonprofit organizations like Gradient
          Learning, we take privacy seriously and have chosen to inform our
          practices with the CCPA. Specifically, this means the below.
        </$Paragraph>

        <$SubsectionTitle size="h3">10.1 Data Subject Rights</$SubsectionTitle>

        <$Paragraph>
          In addition to the rights listed in Section 9, California residents
          have the additional “right to know.” This right allows you to request
          the following information about the personal information that we’ve
          collected about you in the past 12 months:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            Information about Data Collection
            <$UnorderedList>
              <$ListItem>
                The categories of personal information that have been collected.
              </$ListItem>
              <$ListItem>
                The specific pieces of personal information that have been
                collected about you.
              </$ListItem>
              <$ListItem>
                The categories of sources from which we have collected personal
                information.
              </$ListItem>
              <$ListItem>
                The business purpose for which we have collected personal
                information.
              </$ListItem>
            </$UnorderedList>
          </$ListItem>
          <$ListItem>
            Information about Data Disclosure
            <$UnorderedList>
              <$ListItem>
                The categories of personal information that we have sold,
                shared, or disclosed for a business purpose.
              </$ListItem>
              <$ListItem>
                The categories of third parties with whom personal information
                has been sold, shared, or disclosed for a business purpose.
              </$ListItem>
            </$UnorderedList>
          </$ListItem>
        </$UnorderedList>
        <$Paragraph>
          Note that general details about data collection and disclosure are
          provided in this Privacy Policy. If you wish to exercise your rights
          or request more specific details about the above, email&nbsp;
          <$PrivacyAtAlongEmailLink />. and follow the instructions in Section
          9.1 above. If you would like an authorized agent to make a request for
          you, have that agent email&nbsp;
          <strong>
            <$PrivacyAtAlongEmailLink />
          </strong>
          &nbsp;with the above information along with additional information
          sufficient for us to verify that the authorized agent is acting on
          your behalf.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          10.2 Additional Disclosures / “Notice at Collection”
        </$SubsectionTitle>

        <$Paragraph>
          You have a right to receive notice of our personal information
          collection, use, retention, and disclosure practices at or before the
          collection of personal information. We have described in fuller detail
          in this Privacy Policy the personal information that we collect, how
          we use and disclose it, and how long we retain it but provide the
          following overview here:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            <strong>Information we collect</strong>. We have collected the
            following categories of personal information within the past 12
            months: (1) identifiers; (2) internet or other similar network
            activity; (3) education information; (4) inferences drawn from
            internet or other electronic network activity; and (5) audio and
            visual information as part of the content and communications
            exchanged between students and teachers using the Services. See
            Section 3.1 of this Privacy Policy above for more details.
          </$ListItem>
          <$ListItem>
            <strong>Purposes of collection, use, and disclosure</strong>. As
            described in Sections 3.3. and 3.4 above, we do not “sell” or
            “share” personal information (as those terms are defined by the
            CCPA) of the users we have identified as Student Users. We may use
            teacher data for advertising purposes in ways that could be
            considered “sharing” or “selling” under the CCPA. In addition, we
            use or disclose the personal information we collect for one or more
            of the business purposes described in Sections 3.3 and 3.4 above.
          </$ListItem>
          <$ListItem>
            <strong>Retention of information</strong>. We only keep personal
            information for as long as it’s necessary to provide Along. In
            certain cases, we may need to keep personal information if the law
            requires a different duration, or as directed by the teacher or
            school. See Section 7 above for more details.
          </$ListItem>
        </$UnorderedList>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Consumers have the right to request information about the personal
            information we collect. Requests should be sent to&nbsp;
            <$PrivacyAtAlongEmailLink />.
          </$Paragraph>
          <$Paragraph>
            In addition to the details outlined in this Privacy Policy, below
            you’ll find additional disclosure and summary around the personal
            information we collect:
          </$Paragraph>
          <$UnorderedList>
            <$ListItem>
              Over the past 12 months, we’ve collected certain personal
              information from student and teacher users, to provide educational
              services.
            </$ListItem>
            <$ListItem>
              We collect this personal information both from direct and indirect
              sources.
            </$ListItem>
            <$ListItem>
              We may disclose personal information for select business purposes,
              such as to protect against fraudulent activity or to debug our
              website.
            </$ListItem>
          </$UnorderedList>
        </$SummaryBox>

        <$SectionTitle size="h1">11. How We Communicate With You</$SectionTitle>

        <$Paragraph>
          If you created an account on the Services (or otherwise provided an
          email address or phone number to us, or otherwise opted-in to receive
          communications from us), we may send you messages related to the
          Services, including messages regarding your account, privacy and
          security notices, and updates and information regarding the Services.
          These communications may be sent through SMS, push notifications,
          email, telephone calls, and postal mail. [Note: Standard carrier fees
          may apply to messages sent to your mobile devices.] If you have an
          account with us, we’ll also use your contact information for customer
          service purposes, or to contact you for legal matters or purposes. We
          may receive a confirmation when you open an email from us if your
          device supports it.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We may contact you with messages and notifications about Along,
            including information about your account, privacy and security
            notices, and service updates.
          </$Paragraph>
        </$SummaryBox>
        <$Paragraph>
          <strong>For School Administrators</strong>. We require Registered
          Teachers signing up for the Services to tell their School
          Administrator about it. This means a Registered Teacher may provide us
          with your email address so we can send you an email from the
          Registered Teacher telling you about their use of the Services. We may
          also contact you with questions related to your School’s use of the
          Services via your Registered Teachers and Student Users.
        </$Paragraph>
        <$Paragraph>
          <strong>For Parents and Legal Guardians</strong>. We encourage
          Registered Teachers signing up for the Services to tell you about
          their use of the Services in their classroom with your child. This
          means Registered Teachers may provide us your email address and/or
          phone number so we can send you content from the Registered Teacher
          telling you about their use of the Services. We may also contact you
          with questions related to your child’s use of the Services.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Teachers may give us contact information about school administrators
            and parents. We may contact school administrators and parents to
            provide requested information, respond to inquiries, or provide
            information as directed by the school.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">
          12. We Are Transparent About Changes
        </$SectionTitle>

        <$Paragraph>
          We may modify this Privacy Policy or our&nbsp;
          <$UserAgreementLink />
          &nbsp;from time to time. If we make material changes to this Privacy
          Policy, including changes that impair your rights or change how your
          data will be used under this Privacy Policy, we will provide you at
          least 30 days prior notice pursuant to this Section 12. We will seek
          to provide you notice through our Services, or by other means, to
          provide you the opportunity to review the changes before they become
          effective. We agree that changes cannot be retroactive. The notice
          will indicate which sections contain material modifications and what
          choices you may have. Your continued use of our Services after we
          publish or send a notice about our changes to these terms means that
          you are consenting to the updated terms following their “effective
          date.” If you object to any changes, you must stop using or accessing
          the Services. If we make any changes to this Privacy Policy or the
          User Agreement, you can request a copy of the prior versions by
          contacting us at&nbsp;
          <$PrivacyAtAlongEmailLink />.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            When we make material changes to this policy, we will notify you
            before the changes go into effect.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">13. We Want To Hear From You</$SectionTitle>

        <$Paragraph>
          If you have any questions about this Privacy Policy or our practices,
          please contact <$PrivacyAtAlongEmailLink />. If you have any other
          questions, please do not hesitate to contact the iKeepSafe Safe Harbor
          program at&nbsp;
          <$ExternalLink
            href="mailto:COPPAprivacy@ikeepsafe.org"
            linkText="COPPAprivacy@ikeepsafe.org"
            displayExternalLinkIcon={false}
          />
          .
        </$Paragraph>
        <$Paragraph>
          You can learn more about Gradient Learning on our website,&nbsp;
          <$ExternalLink
            href="http://gradientlearning.org/"
            linkText="http://gradientlearning.org/"
          />
          .
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            If you have questions about the Privacy Policy, you can email us
            at&nbsp;
            <$PrivacyAtAlongEmailLink />.
          </$Paragraph>
        </$SummaryBox>
      </$TextContainer>
    </PolicyLayout>
  );
}
